import { ActivityFeedItem } from './pusher.service';
import { listStreamItemsControllerListStreamItems } from '../generated/api';
import { eduleadClient } from './edulead-client';

/**
 * State of the precomputed audience behind a stream.
 *
 * PENDING            - starred or shared, audience not built yet (usually seconds)
 * READY              - members built, stream is servable
 * SKIPPED_OVER_LIMIT - too many matching contacts to stream
 * FAILED             - last rebuild errored
 */
export type StreamAudienceStatus = 'PENDING' | 'READY' | 'SKIPPED_OVER_LIMIT' | 'FAILED';

export interface StreamAudience {
  status: StreamAudienceStatus;
  contactCount: number | null;
  computedAt: string | null;
}

export interface FetchStreamItemsResult {
  items: ActivityFeedItem[];
  nextCursor: string | null;
  audience: StreamAudience;
}

interface StreamItemResponse {
  id: string;
  eventType: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  observedAt: string;
  payload: unknown;
}

export async function fetchStreamItems(
  savedSearchId: string,
  limit = 50,
  cursor?: string
): Promise<FetchStreamItemsResult> {
  const response = await listStreamItemsControllerListStreamItems({
    client: eduleadClient,
    path: { savedSearchId },
    query: { limit, ...(cursor ? { cursor } : {}) },
  });

  const data = response.data as unknown as {
    items: StreamItemResponse[];
    nextCursor: string | null;
    audience: StreamAudience;
  };

  return {
    // Mapped onto ActivityFeedItem so the stream renders through the same FeedItem
    // component as the activity feed. A stream item is ordered by when we recorded the
    // change, which takes the place of the feed's paced delivery time.
    items: (data.items ?? []).map((item) => ({
      id: item.id,
      eventType: item.eventType,
      entityType: item.entityType,
      entityId: item.entityId,
      payload: item.payload,
      deliveredAt: item.createdAt,
    })),
    nextCursor: data.nextCursor ?? null,
    audience: data.audience,
  };
}
